<template>
  <div class="custom-upload-audio customization-upload-file-wrapper">
    <div class="customization-title">
      {{ $props.title }}
    </div>
    <CustomFileUpload></CustomFileUpload>
    <span class="tooltip-image-processing" v-show="loading">
      {{ uploadProcessingText }}
      {{ percentProcessed ? `${percentProcessed}%` : '' }}
    </span>
    <span
      class="remove-custom-upload-image"
      v-show="isSuccess && !loading"
      @click="removeFile"
    >
      X
    </span>
    <span
      class="customization-upload-image-wrapper-message"
      v-show="isShowMessage && !isRemoveFile"
      :class="messageClasses"
    >
      {{ messageUploadAudio }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed, h, onMounted, ref, watch } from 'vue'
import FileUpload from '@/ui-kits/FileSelect.vue'
import useCustomizationStore from '@/store/customization'
import {
  EVENT_CUSTOM_UPLOAD_IMAGE,
  CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS,
  QUERY_STRING_NO_CACHE,
  EVENT_CHANGE_COLOR_QR_CODE,
  EVENT_CHANGE_AUDIO_SOUND_WAVE,
} from '@/utils'
import EventBus from '@/services/eventbus'
import { useProductCustomization } from './composables/ProductCustomization'
import { useRoute } from 'vue-router'
import { NullOrType } from '@/types'

const route = useRoute()
const customizationStore = useCustomizationStore()
const { startUpload, finishUpload, percentProcessed } =
  useProductCustomization()

const props = withDefaults(
  defineProps<{
    id: string
    title: string
    soundWave?: boolean
    qrCode?: boolean
    qrCodeTemplate?: { title: string; values: string[] }
    selectImageButtonCustomily?: any
  }>(),
  {}
)

const emit = defineEmits<{
  (e: 'change-audio-image-qrcode', image: string): void
  (e: 'remove-audio-file'): void
}>()

const messageUploadAudio = computed(() =>
  isSuccess.value ? successfullMessage : errorMessage.value
)
const messageClasses = computed(() =>
  isSuccess.value
    ? 'text-primary'
    : `text-secondary ${CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS}-message--error`
)

const loading = ref(false)
const isSuccess = ref(false)
const isShowMessage = ref(false)
const isRemoveFile = ref(false)
const uploadProcessingText = 'Processing'
const successfullMessage = 'Successful!'
const errorMessage = ref('Error!')
const waveForm = ref([])
const colorQrCode = ref(props.qrCodeTemplate?.values[0] || '#000000')
const fileSelected = ref<NullOrType<File>>(null)

onMounted(async () => {
  watch(
    () => loading.value,
    (value) => {
      EventBus.trigger(EVENT_CUSTOM_UPLOAD_IMAGE, value)
      if (value) startUpload()
      else finishUpload()
    }
  )

  EventBus.on(EVENT_CHANGE_COLOR_QR_CODE, (color) => {
    colorQrCode.value = color
    if (props.qrCode && fileSelected.value && color) {
      loading.value = true
      isSuccess.value = false
      isShowMessage.value = false

      customizationStore
        .generateQrCodeByFile({
          file: fileSelected.value,
          color: colorQrCode.value,
          isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
        })
        .then((imageUr) => {
          if (props.qrCode) {
            emit('change-audio-image-qrcode', imageUr)
          }
          isSuccess.value = true
        })
        .catch((err) => {
          errorMessage.value = err.message ?? errorMessage.value
          isSuccess.value = false
        })
        .finally(() => {
          if (isSuccess.value) {
            customilyUpLoadingHandler()
          } else {
            loading.value = false
            isShowMessage.value = true
          }
        })
    }
  })
})

const CustomFileUpload = () => {
  let tag: any = FileUpload
  const propsCustomFileUpload: {
    [key: string]: any
  } = {}

  propsCustomFileUpload.id = props.id
  propsCustomFileUpload.loading = loading.value
  propsCustomFileUpload.text = 'Select file'
  propsCustomFileUpload.accept =
    '.wav,.aif,.aiff,.flac,.alac,.aac,.ogg,.mp3,.m4a,.amr'
  propsCustomFileUpload.onChange = onChange

  async function onChange(event: Event) {
    const input = event.target as HTMLInputElement
    if (!input.files) return
    const file = input.files[0]
    if (!file) return
    fileSelected.value = file

    loading.value = true
    isSuccess.value = false
    isShowMessage.value = false

    const apis = []
    if (props.qrCode) {
      apis.push(
        customizationStore.generateQrCodeByFile({
          file,
          color: colorQrCode.value,
          isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
        })
      )
    }
    if (props.soundWave) {
      apis.push(
        customizationStore.generateWaveForm({
          type: 'file',
          file,
          isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
        })
      )
    }

    Promise.all(apis)
      .then((response: any) => {
        if (props.soundWave && props.qrCode) {
          waveForm.value = response[1]
          emit('change-audio-image-qrcode', response[0])
          EventBus.trigger(EVENT_CHANGE_AUDIO_SOUND_WAVE, response[1])
        } else if (props.qrCode) {
          emit('change-audio-image-qrcode', response[0])
        } else if (props.soundWave) {
          waveForm.value = response[0]
          EventBus.trigger(EVENT_CHANGE_AUDIO_SOUND_WAVE, response[0])
        }

        isSuccess.value = true
      })
      .catch((err) => {
        errorMessage.value = err.message ?? errorMessage.value
        isSuccess.value = false
      })
      .finally(() => {
        if (isSuccess.value) {
          customilyUpLoadingHandler()
        } else {
          loading.value = false
          isShowMessage.value = true
        }
      })

    input.value = ''
  }

  return h(tag, propsCustomFileUpload)
}

function removeFile() {
  isSuccess.value = false
  isRemoveFile.value = true
  fileSelected.value = null
  emit('remove-audio-file')
}

function customilyUpLoadingHandler() {
  const timeout = setTimeout(() => {
    const isCustomilyUpLoading =
      props.selectImageButtonCustomily?.classList.contains('disabled')
    if (isCustomilyUpLoading) {
      customilyUpLoadingHandler()
    } else {
      loading.value = false
      isShowMessage.value = true
      clearTimeout(timeout)
    }
  }, 200)
}
</script>

<style lang="scss">
.custom-upload-audio.customization-upload-file-wrapper {
  .sib-file-select-wrapper {
    display: inline;
  }
}
</style>
